// src/components/Sucursales/FormularioEdicionSucursal.tsx

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as S from "./FormularioSucursal.styles";

import {
  useForm,
  Controller,
  SubmitHandler,
  FieldErrors,
  UseFormRegister,
} from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiBriefcase,
  FiHash,
  FiChevronDown,
  FiLoader,
  FiCheckCircle,
  FiAlertTriangle,
  FiSave,
} from "react-icons/fi";
import { apiService } from "../../services/apiService";

// --- TIPOS DEL FORMULARIO ---
interface SucursalApiResponse {
  id: number;
  area: number;
  numSucursal: number;
  nombreSuc: string;
  tipoSucursal: string;
  ccPlanta: number;
  ccAgrupacion: number;
  calle: string;
  colonia: string;
  municipio: string;
  estado: string;
  codigoPostal: string;
  telefono: string;
  iva: number;
  serieFactura: string;
  email: string;
  numExt: string;
  numInt: string;
  pais: number;
  ciudad: string;
  ccSAP: string;
  categoria: string;
  impuestos: boolean;
  estatusActivo: boolean | null;
}

type FormValues = {
  nombreSuc: string;
  numSucursal: string;
};

const defaultValues: FormValues = {
  nombreSuc: "",
  numSucursal: "",
};
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: keyof FormValues;
  label: string;
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  icon: React.ReactNode;
}
const FormularioEdicionSucursal = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({ defaultValues, mode: "onBlur" });

  const [isLoading, setIsLoading] = useState(true);
  const [openSections, setOpenSections] = useState<string[]>(["general"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const [dataSource, setDataSource] = useState<SucursalApiResponse[]>([]);

  // Cargar datos por ID
  // src/components/Sucursales/FormularioEdicionSucursal.tsx

// ... (otros imports y código)

  // Cargar datos por ID
  useEffect(() => {
    if (!id) return;

    const fetchSucursalData = async () => {
      setIsLoading(true);
      try {
        const response = await apiService.get<SucursalApiResponse[]>(
          `/api/Catalogos/Sucursales/ById/${id}`
        );

        // Aseguramos que la respuesta sea un array y contenga al menos un elemento
        if (response && response.length > 0) {
          const sucursalData = response[0]; // Obtenemos el primer objeto de la sucursal
          setDataSource(response);

          // ✨ AQUÍ ESTÁ LA MAGIA:
          // Usamos 'reset' para poblar el formulario con los datos de la API.
          // Es importante convertir los valores al tipo esperado por el formulario (ej. numSucursal a string).
          reset({
            nombreSuc: sucursalData.nombreSuc,
            numSucursal: String(sucursalData.numSucursal), // Convertimos el número a string
          });
        }
        
      } catch (error) {
        setSubmitStatus("error");
        setSubmitMessage("No se pudo cargar la información de la sucursal.");
        console.error("Error al cargar la sucursal:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSucursalData();
  }, [id, reset]); // 'reset' ya está en el array de dependencias, lo cual es correcto.

// ... (resto del componente)

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((sId) => sId !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Guardar cambios
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus("idle");

    const payload = {
      ...data,
      id: Number(id),
      numSucursal: Number(data.numSucursal) || 0,
    };

    try {
      await apiService.put(`/api/Catalogos/sucursales/${id}`, payload);
      setSubmitStatus("success");
      setSubmitMessage("¡Sucursal actualizada con éxito!");
      setTimeout(() => navigate("/catalogos/sucursales"), 1500);
    } catch (error) {
      setSubmitStatus("error");
      setSubmitMessage("Error al actualizar la sucursal.");
      console.error("Fallo al actualizar:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <S.FormWrapper>
        <p>Cargando datos de la sucursal...</p>
      </S.FormWrapper>
    );
  }
const Input: React.FC<InputProps> = ({ name, label, register, errors, icon, ...props }) => (
  <S.InputGroup>
    <S.InputLabel htmlFor={name}>{label}</S.InputLabel>
    <S.InputIconWrapper>
      <S.InputIcon>{icon}</S.InputIcon>
      <S.InputField id={name} {...register(name)} {...props} disabled={props.disabled} />
    </S.InputIconWrapper>
    {errors[name] && <S.ErrorText>{errors[name]?.message as string}</S.ErrorText>}
  </S.InputGroup>
);
  return (
    <S.FormWrapper>
      <S.FormContainer onSubmit={handleSubmit(onSubmit)}>
        <S.Header>
          <S.Title>Editar Sucursal</S.Title>
          <S.Subtitle>Modifica los datos existentes de la sucursal.</S.Subtitle>
        </S.Header>

        <S.SectionsContainer>
          <S.FormSection>
            <S.Legend onClick={() => toggleSection("general")}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <FiBriefcase size={22} /> Información General
              </div>
              <S.ChevronWrapper $isOpen={openSections.includes("general")}>
                <FiChevronDown size={24} />
              </S.ChevronWrapper>
            </S.Legend>
            <AnimatePresence>
              {openSections.includes("general") && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1, marginTop: "1.5rem" }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <S.Grid>
                    <Input
                      name="nombreSuc"
                      label="Nombre Sucursal"
                      register={register}
                      errors={errors}
                      icon={<FiBriefcase size={18} />}
                      disabled={isSubmitting}
                    />
                    <Input
                      name="numSucursal"
                      label="Número de Sucursal"
                      type="number"
                      register={register}
                      errors={errors}
                      icon={<FiHash size={18} />}
                      disabled={isSubmitting}
                    />
                  </S.Grid>
                </motion.div>
              )}
            </AnimatePresence>
          </S.FormSection>
        </S.SectionsContainer>

        <S.ButtonWrapper>
          {submitStatus !== "idle" && (
            <S.SubmitMessage status={submitStatus}>
              {submitStatus === "success" ? (
                <FiCheckCircle />
              ) : (
                <FiAlertTriangle />
              )}
              {submitMessage}
            </S.SubmitMessage>
          )}
          <S.SubmitButton type="submit" disabled={isSubmitting || isLoading}>
            {isSubmitting ? (
              <>
                <S.Spinner>
                  <FiLoader />
                </S.Spinner>{" "}
                Guardando...
              </>
            ) : (
              <>
                <FiSave /> Guardar Cambios
              </>
            )}
          </S.SubmitButton>
        </S.ButtonWrapper>
      </S.FormContainer>
    </S.FormWrapper>
  );
};

export default FormularioEdicionSucursal;
